/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState, useMemo } from "react";
import { Button, Flex, Heading, Text, FormLabel } from "@chakra-ui/react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { TimePicker } from "@progress/kendo-react-dateinputs";
import { FaEdit } from "react-icons/fa";
import { MdDeleteSweep } from "react-icons/md";

import ReactHookFormDropdown from "../../../components/forms/react-hook-dropdown";
import { ACTIVITY_TYPES } from "../constants";
import { useLookupCities } from "../../../services/lookups-mutations";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import ReactHookFormMultiSelect from "components/forms/react-hook-multiselect";

export default function CustomerServiceAccessTiming({
  slaDefaults,
  customerServiceAccessTiming = [],
  onPrevStep,
  onNextStep,
}) {
  const [currentEditing, setCurrentEditing] = useState(null);
  const [cities, setCities] = useState([]);

  /* ---------------- FORM ---------------- */

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      city: [],
      activityType: "",
      startTime: null,
      endTime: null,
      accessTiming: customerServiceAccessTiming,
    },
  });

  const {
    fields: accessTimingFields,
    append,
    update,
    remove,
  } = useFieldArray({
    control,
    name: "accessTiming",
  });

  const watchCity = watch("city");
  const watchActivityType = watch("activityType");

  const selectedCity = watchCity?.[0];
  const selectedActivityType = watchActivityType?.id ?? watchActivityType;

  const {
    mutate: getCities,
    data: citiesList,
    reset: resetCitiesList,
  } = useLookupCities();

  useEffect(() => {
    if (!slaDefaults) return;
    if (!selectedCity || !selectedActivityType) return;

    if (currentEditing !== null) return;

    const match = slaDefaults?.serviceWindows?.find(
      (item) =>
        item.city === selectedCity.id &&
        item.activityType === selectedActivityType,
    );

    if (!match) return;

    // store string format, NOT Date
    setValue("startTime", match.startTime ?? null);
    setValue("endTime", match.endTime ?? null);
  }, [slaDefaults, selectedCity, selectedActivityType, currentEditing]);

  useEffect(() => {
    getCities("UAE");
  }, []);

  useEffect(() => {
    if (citiesList) {
      setCities(
        citiesList.data.map((elm) => ({ id: elm.name, text: elm.name })),
      );
      resetCitiesList();
    }
  }, [citiesList]);

  /* ---------------- TIME HELPERS ---------------- */
  const parseBackendTime = (str) => {
    if (!str) return null;
    const [hh, mm, ss] = str.split(":").map(Number);
    const d = new Date();
    d.setHours(hh || 0, mm || 0, ss || 0, 0);
    return d;
  };

  const toBackendTime = (date) => {
    if (!(date instanceof Date) || isNaN(date)) return null;
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}:00`;
  };

  /* Initialize only once */
  useEffect(() => {
    if (customerServiceAccessTiming?.length && !accessTimingFields.length) {
      reset({
        city: [],
        activityType: "",
        startTime: null,
        endTime: null,
        accessTiming: customerServiceAccessTiming,
      });
    }
  }, [customerServiceAccessTiming]);

  // const selectedCitiesInGrid = useMemo(
  //   () => accessTimingFields.map((f) => f.city),
  //   [accessTimingFields],
  // );

  useEffect(() => {
    if (!watchCity?.length || !watchActivityType) return;

    const existingItems = getValues("accessTiming");
    const activityTypeId = watchActivityType?.id || watchActivityType;

    const duplicateExists = watchCity.some((cityObj) =>
      existingItems.some((item, index) => {
        if (currentEditing !== null && index === currentEditing) return false;

        return item.city === cityObj.id && item.activityType === activityTypeId;
      }),
    );

    if (duplicateExists) {
      setError("city", {
        type: "duplicate",
        message: "Same City + Activity Type combination already exists",
      });
    } else {
      clearErrors("city");
    }
  }, [watchCity, watchActivityType]);

  /* ---------------- SUBMIT ---------------- */

  const onSubmit = (values) => {
    clearErrors("city");

    const existingItems = getValues("accessTiming");

    const base = {
      activityType: values.activityType?.id || values.activityType,
      startTime: values.startTime,
      endTime: values.endTime,
    };

    // =============================
    // EDIT MODE
    // =============================
    if (currentEditing !== null) {
      const selectedCity = values.city[0]?.id;

      const duplicateExists = existingItems.some((item, index) => {
        if (index === currentEditing) return false;

        return (
          item.city === selectedCity && item.activityType === base.activityType
        );
      });

      if (duplicateExists) {
        setError("city", {
          type: "duplicate",
          message: "Same City + Activity Type combination already exists",
        });
        return;
      }

      update(currentEditing, {
        ...base,
        city: selectedCity,
      });

      setCurrentEditing(null);
    } else {
      let duplicateFound = false;

      values.city.forEach((city) => {
        const alreadyExists = existingItems.some((item) => {
          return (
            item.city === city.id && item.activityType === base.activityType
          );
        });

        if (alreadyExists) duplicateFound = true;
      });

      if (duplicateFound) {
        setError("city", {
          type: "duplicate",
          message: "Same City + Activity Type combination already exists",
        });
        return;
      }

      values.city.forEach((cityObj) => {
        append({
          ...base,
          city: cityObj.id,
        });
      });
    }

    const updatedFields = getValues("accessTiming");

    reset({
      city: [],
      activityType: "",
      startTime: null,
      endTime: null,
      accessTiming: updatedFields,
    });
  };

  const cancelEditing = () => {
    setCurrentEditing(null);
    reset({
      city: [],
      activityType: "",
      startTime: null,
      endTime: null,
      accessTiming: getValues("accessTiming"),
    });
  };

  /* ---------------- CELLS ---------------- */

  const editCell = ({ dataItem, dataIndex }) => (
    <td>
      <FaEdit
        size={18}
        color="#2B6CB0"
        style={{ cursor: "pointer" }}
        onClick={() => {
          reset({
            city: cities.find((c) => c.id === dataItem.city)
              ? [cities.find((c) => c.id === dataItem.city)]
              : [{ id: dataItem.city, text: dataItem.city }],
            activityType: dataItem.activityType,
            // ACTIVITY_TYPES.find(
            //   (a) => a.id === dataItem.activityType,
            // ) || {
            //   id: dataItem.activityType,
            //   text: dataItem.activityType,
            // },
            startTime: dataItem.startTime,
            endTime: dataItem.endTime,
            accessTiming: getValues("accessTiming"),
          });
          setCurrentEditing(dataIndex);
        }}
      />
    </td>
  );

  const deleteCell = ({ dataIndex }) => (
    <td>
      <MdDeleteSweep
        size={20}
        color="red"
        style={{ cursor: "pointer" }}
        onClick={() => remove(dataIndex)}
      />
    </td>
  );

  /* ---------------- RENDER ---------------- */

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
      <Flex w="100%" justify="space-between" mb="16px">
        <Heading size="md">Customer Service Access Timing</Heading>
        <Flex gap="16px">
          <Button
            type="button"
            onClick={() => onPrevStep(getValues("accessTiming"))}
          >
            Back
          </Button>
          <Button
            type="button"
            onClick={() => onNextStep(getValues("accessTiming"))}
          >
            Next
          </Button>
        </Flex>
      </Flex>

      {/* CITY */}
      <ReactHookFormMultiSelect
        control={control}
        errors={errors}
        name="city"
        label="City"
        isRequired
        filterable
        id="id"
        text="text"
        data={cities}
        placeholder="Select City"
        rules={{
          validate: (value) =>
            value?.length > 0 || "Please select at least one city",
        }}
      />

      <ReactHookFormDropdown
        control={control}
        errors={errors}
        isRequired
        label="Activity Type"
        name="activityType"
        filterable
        id="id"
        text="text"
        data={ACTIVITY_TYPES}
        placeholder="Select Type"
        rules={{ required: "Activity Type is required" }}
      />

      {/* TIME */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "300px 220px",
          gap: "24px",
          marginBottom: "24px",
        }}
      >
        <div>
          <FormLabel fontSize="sm">Start Time *</FormLabel>
          <Controller
            name="startTime"
            control={control}
            rules={{ required: "Start Time is required" }}
            render={({ field }) => (
              <TimePicker
                value={parseBackendTime(field.value)}
                onChange={(e) => field.onChange(toBackendTime(e.value))}
                format="HH:mm"
                style={{ width: "100%" }}
              />
            )}
          />
        </div>

        <div>
          <FormLabel fontSize="sm">End Time *</FormLabel>
          <Controller
            name="endTime"
            control={control}
            rules={{ required: "End Time is required" }}
            render={({ field }) => (
              <TimePicker
                value={parseBackendTime(field.value)}
                onChange={(e) => field.onChange(toBackendTime(e.value))}
                format="HH:mm"
                style={{ width: "100%" }}
              />
            )}
          />
        </div>
      </div>

      {/* ACTIONS */}
      <Flex justify="flex-start" mb={4} width="100%" gap={4}>
        {currentEditing !== null && (
          <Button variant="outline" onClick={cancelEditing}>
            Cancel
          </Button>
        )}
        <Button colorScheme="blue" type="submit">
          {currentEditing !== null
            ? "Update Access Timing"
            : "Add Access Timing"}
        </Button>
      </Flex>

      {/* GRID */}
      <Grid
        data={getValues("accessTiming")}
        style={{ border: "1px solid #E2E8F0", borderRadius: "8px" }}
      >
        <GridColumn field="city" title="City" />
        <GridColumn field="activityType" title="Activity Type" />
        <GridColumn field="startTime" title="Start Time" />
        <GridColumn field="endTime" title="End Time" />
        <GridColumn width={80} title="Edit" cell={editCell} />
        <GridColumn width={80} title="Delete" cell={deleteCell} />
      </Grid>
    </form>
  );
}
